package com.tregz.polyglot.io.cloud

import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.auth.auth
import dev.gitlive.firebase.firestore.firestore
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlinx.serialization.Serializable

@Serializable
data class PolyglotBundle(
    val version: Int = 1,
    val strings: Map<String, String> = emptyMap()
)

/** Outcome of a cache lookup: a miss (translate) is distinct from an error (do not pay again). */
sealed interface PolyglotCacheResult {
    data class Hit(val strings: Map<String, String>) : PolyglotCacheResult
    data object Miss : PolyglotCacheResult
    data class Error(val message: String?) : PolyglotCacheResult
}

interface PolyglotCache {
    suspend fun retrieve(collectionPath: String, languageCode: String, expectedVersion: Int): Map<String, String>?
    suspend fun upload(collectionPath: String, languageCode: String, bundle: PolyglotBundle)

    /** Distinguishes hit, miss and error. Defaults to [retrieve] for custom implementations. */
    suspend fun lookup(collectionPath: String, languageCode: String, expectedVersion: Int): PolyglotCacheResult {
        val strings = retrieve(collectionPath, languageCode, expectedVersion)
        return if (strings.isNullOrEmpty()) PolyglotCacheResult.Miss else PolyglotCacheResult.Hit(strings)
    }

    companion object {
        val default: PolyglotCache by lazy { PolyglotFirestoreCache() }
    }
}

class PolyglotFirestoreCache : PolyglotCache {
    private val authMutex = Mutex()

    private suspend fun ensureAuth() {
        authMutex.withLock {
            try {
                ensureFirebaseInitialized()
                if (Firebase.auth.currentUser == null) {
                    Firebase.auth.signInAnonymously()
                }
            } catch (ex: CancellationException) {
                throw ex
            } catch (ex: Throwable) {
                println("[PolyglotCache] Auth warning: ${ex.message}")
            }
        }
    }

    override suspend fun lookup(
        collectionPath: String,
        languageCode: String,
        expectedVersion: Int
    ): PolyglotCacheResult {
        return try {
            ensureAuth()
            val document = Firebase.firestore.collection(collectionPath).document(languageCode)
            val snapshot = document.get()
            if (!snapshot.exists) {
                println("[PolyglotCache] Document '$collectionPath/$languageCode' does not exist")
                return PolyglotCacheResult.Miss
            }
            val version = snapshot.get<Int?>("version")
            when {
                version == null || version < expectedVersion -> {
                    // Outdated: never delete the shared doc from a client; the caller overwrites it.
                    println("[PolyglotCache] Outdated version $version (expected $expectedVersion)")
                    PolyglotCacheResult.Miss
                }
                version > expectedVersion -> {
                    // Written by a newer client: leave it untouched and keep defaults.
                    PolyglotCacheResult.Error("Stored version $version is newer than $expectedVersion")
                }
                else -> {
                    val bundle = snapshot.data<PolyglotBundle>()
                    println("[PolyglotCache] Retrieved ${bundle.strings.size} strings for '$languageCode'")
                    PolyglotCacheResult.Hit(bundle.strings)
                }
            }
        } catch (ex: CancellationException) {
            throw ex
        } catch (ex: Throwable) {
            println("[PolyglotCache] Error retrieving strings: ${ex.message}")
            PolyglotCacheResult.Error(ex.message)
        }
    }

    override suspend fun retrieve(
        collectionPath: String,
        languageCode: String,
        expectedVersion: Int
    ): Map<String, String>? {
        return (lookup(collectionPath, languageCode, expectedVersion) as? PolyglotCacheResult.Hit)?.strings
    }

    override suspend fun upload(
        collectionPath: String,
        languageCode: String,
        bundle: PolyglotBundle
    ) {
        try {
            ensureAuth()
            val ref = Firebase.firestore.collection(collectionPath).document(languageCode)
            val existing = ref.get()
            if (existing.exists) {
                val stored = existing.get<Int?>("version")
                if (stored != null && stored > bundle.version) {
                    println("[PolyglotCache] Skipping upload: stored version $stored is newer than ${bundle.version}")
                    return
                }
            }
            ref.set(bundle)
            println("[PolyglotCache] Uploaded ${bundle.strings.size} strings to '$collectionPath/$languageCode'")
        } catch (ex: CancellationException) {
            throw ex
        } catch (ex: Throwable) {
            println("[PolyglotCache] Error uploading strings: ${ex.message}")
        }
    }
}
